import { memo } from 'react';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { getRaceUnitName } from '../../constants/game';
import type { Outcome } from './WarRoomOutcome';
import type { Province } from '../../types/province';
import type { ArmyComposition } from '../../types/attack';
import type { UnitPool } from '../../utils/minimumWinningForce';

const GENERALS_OPTIONS = [1, 2, 3, 4] as const;

interface WarRoomTroopControlsProps {
  province: Province;
  /**
   * SF-183 Component 7: at-home-only troop pool (see `useWarRoomState.ts`'s `buildUnitPool`) —
   * input `max` caps must source from this, not `province.population.*` (away-inclusive), so
   * Manual-mode input can no longer offer units already committed to another attack.
   */
  unitPool: UnitPool;
  mode: 'auto' | 'manual';
  onSwitchToAuto: () => void;
  onSwitchToManual: () => void;
  autoSwitchReason: 'impossible' | null;
  isImpossible: boolean;
  verdict: Outcome;
  generals: number;
  oversendPct: number;
  composition: ArmyComposition;
  includeSoldiers: boolean;
  includeMercenaries: boolean;
  targetOP: number | null;
  onGeneralsChange: (generals: number) => void;
  onOversendChange: (pct: number) => void;
  onFieldChange: (field: keyof ArmyComposition, value: number) => void;
  onToggleSoldiers: (on: boolean) => void;
  onToggleMercenaries: (on: boolean) => void;
}

export const WarRoomTroopControls = memo(function WarRoomTroopControls({
  province,
  unitPool,
  mode,
  onSwitchToAuto,
  onSwitchToManual,
  autoSwitchReason,
  isImpossible,
  verdict,
  generals,
  oversendPct,
  composition,
  includeSoldiers,
  includeMercenaries,
  targetOP,
  onGeneralsChange,
  onOversendChange,
  onFieldChange,
  onToggleSoldiers,
  onToggleMercenaries,
}: WarRoomTroopControlsProps) {
  const race = province.metadata.race;
  const offspecName = getRaceUnitName(race, 'offense');
  const eliteName = getRaceUnitName(race, 'elite');

  const isAuto = mode === 'auto';
  const noSohdYet = targetOP === null;
  const stillImpossible = isAuto && !noSohdYet && isImpossible;

  const targetLabel = targetOP !== null
    ? `Targeting ${formatNumber(Math.round(targetOP))} OP`
    : null;

  const soldiersWarning = composition.soldiers > 0 ? 'Drawing from soldiers reduces your home defense.' : null;
  const mercenariesNote = composition.mercenaries > 0 ? 'Mercenary cost: 25 gc/unit.' : null;

  return (
    <div
      className="rounded p-3 space-y-3"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          Your Troops
        </div>
        <div className="flex items-center gap-2">
          {/* SF-174 Component 5: explicit Auto/Manual mode — role="radiogroup", matching this
              file's existing Generals-control precedent (same pattern, verbatim keyboard model). */}
          <div role="radiogroup" aria-label="Troop mode" className="flex gap-1.5">
            <button
              type="button"
              role="radio"
              aria-checked={isAuto}
              onClick={onSwitchToAuto}
              className="rounded text-sm font-semibold px-3"
              style={{
                minHeight: 44,
                border: `1px solid ${isAuto ? COLORS.link : COLORS.border}`,
                backgroundColor: isAuto ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
                color: isAuto ? COLORS.link : COLORS.text.secondary,
              }}
            >
              Auto
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!isAuto}
              onClick={onSwitchToManual}
              className="rounded text-sm font-semibold px-3"
              style={{
                minHeight: 44,
                border: `1px solid ${!isAuto ? COLORS.link : COLORS.border}`,
                backgroundColor: !isAuto ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
                color: !isAuto ? COLORS.link : COLORS.text.secondary,
              }}
            >
              Manual
            </button>
          </div>
          {/* SF-174: mobile verdict-visibility fix — a coarse win/lose signal stays visible at the
              point of editing troops, without a second sticky region. Rendered on both platforms.
              No `role="status"` here — that's an implicit aria-live region, and WarRoomOutcome.tsx
              already wraps the identical Win/Lose/Unknown value in its own aria-live="polite"
              region (CLAUDE.md's "don't pair a native role='status' with a separate live-region
              announcer for the same content" rule); a second live region here would double-announce
              every verdict flip. Left in the accessibility tree (no aria-hidden) so a screen reader
              user who navigates directly to this badge can still read it — it just isn't
              auto-announced on every change the way WarRoomOutcome's region is. */}
          <span
            data-testid="warroom-troop-verdict-badge"
            className="text-xs font-bold rounded px-2"
            style={{
              minHeight: 44,
              display: 'inline-flex',
              alignItems: 'center',
              border: `1px solid ${verdict === 'Win' ? COLORS.positive : verdict === 'Lose' ? COLORS.negative : COLORS.text.muted}`,
              color: verdict === 'Win' ? COLORS.positive : verdict === 'Lose' ? COLORS.negative : COLORS.text.muted,
            }}
          >
            {verdict}
          </span>
        </div>
      </div>

      {autoSwitchReason === 'impossible' && (
        <div
          className="text-xs rounded px-2 py-1.5"
          style={{ backgroundColor: '#2a2000', border: '1px solid #5a4400', color: COLORS.warning }}
        >
          Switched to Manual — this target can't be beaten with your current army
        </div>
      )}

      {/* Generals — a discrete 4-value segmented control. */}
      <div className="space-y-1">
        <label className="text-xs" style={{ color: COLORS.text.tertiary }}>
          Generals
        </label>
        <div role="radiogroup" aria-label="Generals" className="flex gap-1.5">
          {GENERALS_OPTIONS.map((g) => {
            const isSelected = g === generals;
            return (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onGeneralsChange(g)}
                className="flex-1 rounded text-sm font-semibold"
                style={{
                  minHeight: 44,
                  border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
                  backgroundColor: isSelected ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
                  color: isSelected ? COLORS.link : COLORS.text.secondary,
                }}
              >
                {g}
              </button>
            );
          })}
        </div>
      </div>

      {/* Unit rows — same 6, same order, same position in both modes, so switching tabs never
          reflows the card. Auto shows two empty states in place of the rows when there's nothing
          useful to compute yet. */}
      {isAuto && noSohdYet ? (
        <div className="text-sm" style={{ color: COLORS.text.muted }}>
          Run SOHD to auto-size your army
        </div>
      ) : stillImpossible ? (
        <div className="text-sm" style={{ color: COLORS.negative }}>
          Can't beat this target's defense with your full army — lower Oversend % or switch to
          Manual to send everything anyway.
        </div>
      ) : (
        <div className="space-y-1.5">
          <UnitRow
            label={offspecName}
            value={composition.offenseSpecialists}
            max={unitPool.offenseSpecialists}
            field="offenseSpecialists"
            onChange={onFieldChange}
            readOnly={isAuto}
            warningText={null}
          />
          <UnitRow
            label={eliteName}
            value={composition.elites}
            max={unitPool.elites}
            field="elites"
            onChange={onFieldChange}
            readOnly={isAuto}
            warningText={null}
          />
          <UnitRow
            label="War Horses"
            value={composition.warHorses}
            max={unitPool.warHorses}
            field="warHorses"
            onChange={onFieldChange}
            readOnly={isAuto}
            warningText={null}
          />
          <UnitRow
            label="Prisoners"
            value={composition.prisoners}
            max={unitPool.prisoners}
            field="prisoners"
            onChange={onFieldChange}
            readOnly={isAuto}
            warningText={null}
          />
          {isAuto ? (
            <GatedUnitRow
              label="Soldiers"
              value={composition.soldiers}
              max={unitPool.soldiers}
              field="soldiers"
              checked={includeSoldiers}
              onToggleChecked={onToggleSoldiers}
              warningText={soldiersWarning}
            />
          ) : (
            <UnitRow
              label="Soldiers"
              value={composition.soldiers}
              max={unitPool.soldiers}
              field="soldiers"
              onChange={onFieldChange}
              warningText={soldiersWarning}
            />
          )}
          {isAuto ? (
            <GatedUnitRow
              label="Mercenaries"
              value={composition.mercenaries}
              max={10000}
              field="mercenaries"
              checked={includeMercenaries}
              onToggleChecked={onToggleMercenaries}
              warningText={mercenariesNote}
            />
          ) : (
            <UnitRow
              label="Mercenaries"
              value={composition.mercenaries}
              max={10000}
              field="mercenaries"
              onChange={onFieldChange}
              warningText={mercenariesNote}
            />
          )}
        </div>
      )}

      {/* Oversend % — Auto-only; doesn't apply once hand-picking numbers in Manual. */}
      {isAuto && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs" style={{ color: COLORS.text.tertiary }}>
              Oversend %
            </label>
            {targetLabel && (
              <span className="text-xs" style={{ color: COLORS.text.muted }}>{targetLabel}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              aria-label="Decrease oversend percentage"
              className="rounded text-sm font-bold"
              style={{ width: 44, height: 44, backgroundColor: COLORS.sectionHeader, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
              onClick={() => onOversendChange(Math.max(0, oversendPct - 0.5))}
            >
              −
            </button>
            <input
              type="number"
              min={0}
              max={20}
              step={0.5}
              value={oversendPct}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v >= 0 && v <= 20) onOversendChange(v);
              }}
              className="w-16 text-center rounded text-sm px-1 py-0.5"
              style={{ ...INPUT_STYLE, minHeight: 44 }}
            />
            <button
              aria-label="Increase oversend percentage"
              className="rounded text-sm font-bold"
              style={{ width: 44, height: 44, backgroundColor: COLORS.sectionHeader, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
              onClick={() => onOversendChange(Math.min(20, oversendPct + 0.5))}
            >
              +
            </button>
            <span className="text-xs" style={{ color: COLORS.text.muted }}>%</span>
          </div>
        </div>
      )}

      {/* Troop summary */}
      {(composition.offenseSpecialists > 0 || composition.elites > 0 || composition.warHorses > 0 || composition.prisoners > 0 || composition.soldiers > 0 || composition.mercenaries > 0) && (
        <div className="text-xs space-y-0.5 pt-1.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          {composition.prisoners > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>Prisoners</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.prisoners)}</span>
            </div>
          )}
          {composition.offenseSpecialists > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>{offspecName}</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.offenseSpecialists)}</span>
            </div>
          )}
          {composition.elites > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>{eliteName}</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.elites)}</span>
            </div>
          )}
          {composition.warHorses > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>War Horses</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.warHorses)}</span>
            </div>
          )}
          {composition.soldiers > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>Soldiers</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.soldiers)}</span>
            </div>
          )}
          {composition.mercenaries > 0 && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>Mercenaries</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(composition.mercenaries)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

interface UnitRowProps {
  label: string;
  value: number;
  max: number;
  field: keyof ArmyComposition;
  onChange: (field: keyof ArmyComposition, value: number) => void;
  readOnly?: boolean;
  warningText: string | null;
}

/** Row label format: `Label (max)` — one shared flush-left label column, flush-right input. */
function UnitRow({ label, value, max, field, onChange, readOnly, warningText }: UnitRowProps) {
  const inputId = `warroom-unit-${field}`;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-2">
        <label htmlFor={inputId} className="text-xs w-28 shrink-0" style={{ color: COLORS.text.tertiary }}>
          {label} ({formatNumber(max)})
        </label>
        <input
          id={inputId}
          type="number"
          min={0}
          max={max}
          value={value}
          readOnly={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v)) onChange(field, Math.max(0, Math.min(max, v)));
          }}
          className="w-24 rounded text-sm px-1 py-0.5 text-right"
          style={{ ...INPUT_STYLE, minHeight: 44, opacity: readOnly ? 0.75 : 1 }}
        />
      </div>
      <div className="text-xs pl-28" style={{ color: COLORS.warning, minHeight: '1.1em' }}>
        {warningText ?? ' '}
      </div>
    </div>
  );
}

interface GatedUnitRowProps {
  label: string;
  value: number;
  max: number;
  field: keyof ArmyComposition;
  checked: boolean;
  onToggleChecked: (on: boolean) => void;
  warningText: string | null;
}

/**
 * Auto-only: Soldiers/Mercenaries rows start greyed and inert. Uses `aria-disabled` plus a
 * guarding `onClick` condition, not native `disabled` (CLAUDE.md's standing touch-accessibility
 * convention — a control that must stay visible-but-inert keeps DOM/tab-order presence). The
 * Include checkbox that un-greys the row renders inline on the row itself — the label doubles as
 * the toggle target. Once checked, the row joins the MWF solver's pool but stays solver-computed
 * like the other 4 Auto rows — the input is never directly editable here, only the checkbox is
 * interactive (typing a count is Manual's opt-in mechanism, not Auto's).
 */
function GatedUnitRow({ label, value, max, field, checked, onToggleChecked, warningText }: GatedUnitRowProps) {
  const inputId = `warroom-unit-${field}`;
  const toggleId = `${inputId}-toggle`;
  const reasonId = `${inputId}-reason`;
  const inert = !checked;

  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-2">
        <label htmlFor={toggleId} className="text-xs w-28 shrink-0 flex items-center gap-1 cursor-pointer" style={{ color: COLORS.text.tertiary }}>
          <input id={toggleId} type="checkbox" checked={checked} onChange={(e) => onToggleChecked(e.target.checked)} />
          <span>{label} ({formatNumber(max)})</span>
        </label>
        <input
          id={inputId}
          aria-label={`${label} count`}
          type="number"
          min={0}
          max={max}
          value={value}
          readOnly
          aria-disabled={inert || undefined}
          aria-describedby={inert ? reasonId : undefined}
          onChange={() => {
            // Solver-computed once checked — the input is always readOnly; only the checkbox
            // above is interactive. No onChange handler needed beyond the controlled-input no-op.
          }}
          onClick={(e) => {
            if (inert) e.preventDefault();
          }}
          className="w-24 rounded text-sm px-1 py-0.5 text-right"
          style={{ ...INPUT_STYLE, minHeight: 44, opacity: inert ? 0.5 : 0.85 }}
        />
      </div>
      <div
        id={inert ? reasonId : undefined}
        className="text-xs pl-28"
        style={{ color: inert ? COLORS.text.muted : COLORS.warning, minHeight: '1.1em' }}
      >
        {inert ? 'Enable via the Include toggle on this row.' : (warningText ?? ' ')}
      </div>
    </div>
  );
}
